/**
 * dealLadder.js — the Direction C unified deal ladder + shared vocabulary
 * (RW-1074, "Build Instructions — Direction C (One Pipeline)").
 *
 * ⚠ HAND-MIRRORED TWIN: functions/src/util/dealLadder.js (server). Any change
 * here must be applied there too — same data, CommonJS exports.
 *
 * The whole Direction C bet: listings and transactions are stages of ONE deal.
 * Seven structural keys, identical for every record —
 *   lead → prep → active → offer → contract → ctc → closed
 * — and the visible label is a function of side + dealType ONLY. Relabel,
 * never fork.
 *
 * app.deals.stage keeps the established RW-827 vocabulary (pre_listing /
 * active / offer / under_contract / lease_uc / closing / post_closing, plus
 * 'lead'); daStageKey() maps stored stage → structural key and
 * legacyStageForKey() maps back for writes, so every pre-Direction-C surface,
 * template stage tag, and the RW-916 cascade keep working off the same column.
 *
 * Self-contained on purpose: no imports, values inlined (TDZ rule —
 * CLAUDE.md Gotcha #11). This module IS the Direction C token source.
 */

// ── tokens (build doc §5 — warm editorial, never dark) ──────────────────
export const DA_COLORS = {
  coral: '#EE8172', coralDark: '#D4685A', coralInk: '#A63F33',
  indigo: '#4F46E5', sky: '#0284C7',
  moss: '#3B5D44', gold: '#C89B3C', purple: '#5C50A1',
  ink: '#15130F', body: '#2A2622', muted: '#6B655C',
  line: '#E8E2DA', cream: '#F0EEE9', surface: '#F6F3EE', surfaceAlt: '#FAF7F2',
  ok: '#10B981', warn: '#F59E0B', err: '#EF4444', info: '#3B82F6',
  grey: '#9C9FA4',
};

// ── the seven structural keys ───────────────────────────────────────────
export const DA_STAGE_KEYS = ['lead', 'prep', 'active', 'offer', 'contract', 'ctc', 'closed'];

// Board-column config (neutral labels — the kanban's seven columns).
export const DA_STAGES = {
  lead:     { label: 'Lead',              color: DA_COLORS.grey },
  prep:     { label: 'Prep / Coming Soon', color: DA_COLORS.coral },
  active:   { label: 'Active',            color: DA_COLORS.moss },
  offer:    { label: 'Offer Out',         color: DA_COLORS.gold },
  contract: { label: 'Under Contract',    color: DA_COLORS.purple },
  ctc:      { label: 'Clear to Close',    color: DA_COLORS.info },
  closed:   { label: 'Closed',            color: DA_COLORS.ok },
};

// One ladder, relabelled — the build-doc §2a table verbatim. null = the key
// doesn't apply to that side (lead is buyer-shaped, prep is listing-shaped).
export const DA_LADDERS = {
  'sale-buyer':    { lead: 'Lead', prep: null,          active: 'Searching', offer: 'Offer',       contract: 'Under Contract', ctc: 'Clear to Close', closed: 'Closed' },
  'sale-listing':  { lead: null,   prep: 'Coming Soon', active: 'On Market', offer: 'Offer',       contract: 'Under Contract', ctc: 'Clear to Close', closed: 'Closed' },
  'lease-buyer':   { lead: 'Lead', prep: null,          active: 'Touring',   offer: 'Application', contract: 'Lease Out',      ctc: 'Approved',       closed: 'Leased' },
  'lease-listing': { lead: null,   prep: 'Listed',      active: 'Showing',   offer: 'Application', contract: 'Lease Out',      ctc: 'Approved',       closed: 'Leased' },
};

/**
 * The relabelled ladder for a deal: [{ key, label }] in board order, keys
 * that don't apply to the side omitted. Non-sale/lease deal types
 * (new_construction, vacant_land, commercial) ride the sale labels; dual and
 * referral sides ride the listing labels — structure never changes.
 */
export function daLadder(side, dealType) {
  const s = side === 'buyer' ? 'buyer' : 'listing';
  const t = dealType === 'lease' ? 'lease' : 'sale';
  const ladder = DA_LADDERS[`${t}-${s}`];
  return DA_STAGE_KEYS
    .filter((key) => ladder[key])
    .map((key) => ({ key, label: ladder[key] }));
}

// ── stored stage ⇄ structural key ───────────────────────────────────────
// app.deals.stage vocabulary (RW-827 templates + DealDetail STAGE_ORDER)
// mapped onto the ladder. Canonical keys map to themselves so callers can
// pass either vocabulary.
const LEGACY_STAGE_TO_KEY = {
  lead: 'lead',
  pre_listing: 'prep', prep: 'prep',
  active: 'active',
  offer: 'offer',
  under_contract: 'contract', lease_uc: 'contract', contract: 'contract',
  closing: 'ctc', ctc: 'ctc',
  post_closing: 'closed', closed: 'closed',
};

// Writes from Direction C surfaces store the LEGACY value — templates' task
// stage tags, the RW-916 'under_contract' cascade, and DealDetail's
// STAGE_ORDER all key off it. (Lease relabelling is display-only; lease_uc
// is never written by the new surfaces.)
const KEY_TO_LEGACY_STAGE = {
  lead: 'lead', prep: 'pre_listing', active: 'active', offer: 'offer',
  contract: 'under_contract', ctc: 'closing', closed: 'post_closing',
};

/** Structural key for a stored stage value (null for unknown/empty). */
export function daStageKey(stage) {
  return LEGACY_STAGE_TO_KEY[stage] || null;
}

/** Stored app.deals.stage value for a structural key (null for unknown). */
export function legacyStageForKey(key) {
  return KEY_TO_LEGACY_STAGE[key] || null;
}

/** Board key for a whole deal — a terminal outcome wins over the working stage. */
export function daStageKeyForDeal(deal) {
  if (!deal) return null;
  if (deal.outcome === 'closed') return 'closed';
  if (deal.outcome) return null; // cancelled / fell_through / expired — off the board
  return daStageKey(deal.stage);
}

// ── payout: the money's PARALLEL lifecycle (build doc §2c) ──────────────
// Independent of compliance and of deal_financials.status (the commission
// APPROVAL machine). payout_held = received but file incomplete — the
// first-class red "disbursement held" state.
export const DA_PAYOUT_ORDER = ['awaiting_file', 'file_submitted', 'approved', 'received', 'funded'];
export const DA_PAYOUT = {
  awaiting_file:  { label: 'Awaiting file' },
  file_submitted: { label: 'File submitted' },
  approved:       { label: 'Approved' },
  received:       { label: 'Received' },
  funded:         { label: 'Funded' },
};

// ── compliance: the docs track (5 states, RW-1076) ──────────────────────
export const DA_COMPLIANCE = {
  none:     { label: 'Not started' },
  pending:  { label: 'Pending review' },
  review:   { label: 'In review' },
  changes:  { label: 'Changes requested' },
  approved: { label: 'Broker approved' },
};

/**
 * Roll deal_compliance_docs statuses up to the deal-level 5-state. Pure;
 * derived every read — never stored, so it can't drift from the docs.
 */
export function complianceRollup(docStatuses, needed = null) {
  const active = (docStatuses || []).filter((s) => s && s !== 'not_applicable');
  if (!active.length) return 'none';
  // RW-3542 / RW-3543: a required item with nothing filed for it keeps the file
  // collecting — never "approved" (every document so far approved) nor "in review"
  // (one document turned in). `needed` comes from the Compliance tab's own list
  // (requiredDocs.evaluate summary.needed); null when that list isn't known.
  const missing = Number.isInteger(needed) && needed > 0;
  if (active.every((s) => s === 'approved')) return missing ? 'pending' : 'approved';
  if (active.some((s) => s === 'rejected')) return 'changes';
  if (active.some((s) => s === 'submitted' || s === 'pending_review' || s === 'needs_signature')) return missing ? 'pending' : 'review';
  return 'pending';
}

// ── forecast (RW-927 — the pulse band's math) ───────────────────────────
// Probability weight per structural key. Defaults per the RW-927 table
// (lead=Outreach 5%, active=Searching 20%, offer 60%, contract 85%,
// closed 100%) with prep/ctc interpolated. Admin overrides live in the
// Firestore doc settings/dealForecast { weights: { key: 0..1 } } — read at
// render, defaults win when unset.
export const DA_STAGE_WEIGHTS = {
  lead: 0.05, prep: 0.10, active: 0.20, offer: 0.60,
  contract: 0.85, ctc: 0.95, closed: 1,
};

// RW-2142: our side of the commission when a record carries no financials of
// its own. One definition — the pipeline pulse, the Production scoreboard and
// Home all read it, so a rate change lands everywhere at once instead of
// drifting across copies (the Production scoreboard carried its own 3%).
export const DA_DEFAULT_RATE_PCT = 2.5;

/**
 * The RW-927 forecast for a single record — for surfaces that hold listings,
 * buyers or legacy transactions rather than One Pipeline deals.
 *
 * `stageKey` is a DA_STAGE_WEIGHTS key; `ratePct` is OUR side of the
 * commission, never the full gross. Returns 0 for a value or a key the model
 * has nothing to say about, so callers can sum without guarding.
 */
export function weightedGci(value, stageKey, { weights = DA_STAGE_WEIGHTS, ratePct = DA_DEFAULT_RATE_PCT } = {}) {
  const v = typeof value === 'string' ? parseFloat(value) : value;
  const w = weights[stageKey];
  if (!Number.isFinite(v) || v <= 0 || !Number.isFinite(w)) return 0;
  return v * (ratePct / 100) * w;
}

// ── luxury (RW-1508 — Clint 2026-07-22: price-derived at $800k+) ────────
// Named constant, single source for every LUXURY badge/filter; per-market
// overrides (NOLA/BR/Northshore) can layer on later — never scatter the
// literal. Lease deals never read as luxury (callers gate on deal_type).
export const DA_LUXURY_THRESHOLD = 800000;

/** Price-derived luxury test — accepts numbers or numeric strings. */
export function isLuxury(value) {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) && n >= DA_LUXURY_THRESHOLD;
}

const toNumber = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

/**
 * The number a deal card and the forecast run on: final price when closed,
 * contract price under contract, the linked listing's ask, else the buyer's
 * search budget. Null when none exist (row shows an em dash, forecast skips).
 */
export function dealValue(deal) {
  if (!deal) return null;
  return dealPrice(deal)
    ?? toNumber(deal.search && deal.search.budget != null ? deal.search.budget : null);
}

/**
 * RW-3767: what a deal adds to pipeline VOLUME — dealValue without the buyer's
 * search budget. A budget is what a buyer could spend, not a price on a deal,
 * so "Pipeline volume" grew every time a search was opened. (The forecast
 * still weighs a search's budget — RW-927's Searching 20% is meant for it.)
 */
export function dealPrice(deal) {
  if (!deal) return null;
  return toNumber(deal.sale_price ?? deal.contract_price ?? deal.listing_price);
}

/**
 * RW-3414: the unit after a lease's price — '/mo' or ''. A lease made in
 * Workspace is priced in monthly rent (RW-2355: "Monthly rent" on New Deal and
 * the listing form). An imported lease (import_source set) carries Darwin's
 * price, which is the monthly rent on only ~55% of leases and the year's rent
 * on most of the rest (dotloop/dealMatching.js pairPrice), so it claims no
 * unit — "$234,000/mo" was a year's rent read as a month's. '' for a sale.
 */
export function leasePriceSuffix(deal) {
  if (!deal || deal.deal_type !== 'lease') return '';
  return deal.import_source ? '' : '/mo';
}

/**
 * RW-3766: a deal's projected GCI — OUR side's gross commission, the one
 * meaning "Proj. GCI" has on the board, the table, the deal header and the
 * People book (never the agent's take after the split). The figure on file
 * (deal_financials.our_side_gross, from the commission engine) when there is
 * one; else, for an open deal only, the forecast's estimate: dealValue × our
 * side's rate (half the deal's gross_rate, else the default). A closed or lost
 * deal with nothing on file, and a co-agent's row, project nothing (null).
 * Unweighted — the pulse's Projected GCI weights each one by its stage.
 */
export function dealProjectedGci(deal, { defaultRatePct = DA_DEFAULT_RATE_PCT } = {}) {
  if (!deal || deal.co_agent) return null;
  const onFile = typeof deal.our_side_gross === 'string' ? parseFloat(deal.our_side_gross) : deal.our_side_gross;
  if (Number.isFinite(onFile) && onFile > 0) return onFile;
  if (deal.outcome) return null;
  const v = dealValue(deal);
  if (!v || v <= 0) return null;
  const rate = Number.isFinite(parseFloat(deal.gross_rate))
    ? parseFloat(deal.gross_rate) / 2 // our side of a full gross rate
    : defaultRatePct;
  return v * (rate / 100);
}

/**
 * The RW-927 pulse, shared by the pipeline (RW-1077) and Home (RW-1080):
 * active count, volume, under-contract count + pending volume, probability-
 * weighted projected GCI, and the needs-you count. `keyOf(deal)` lets the
 * board pass its optimistic-move override; defaults to daStageKeyForDeal.
 */
// RW-2941: a deal the viewer only co-agented (imported history, read-only) is
// the owner's record — it rides the board, never the viewer's numbers
const ownBook = (deals) => (deals || []).filter((d) => !d.co_agent);

export function computeDealsPulse(allDeals, { weights = DA_STAGE_WEIGHTS, defaultRatePct = DA_DEFAULT_RATE_PCT, keyOf, toCheck = 0 } = {}) {
  const deals = ownBook(allDeals);
  const key = keyOf || daStageKeyForDeal;
  const live = deals.filter((d) => key(d) && key(d) !== 'closed');
  // RW-3767: prices only — a buyer search's budget isn't volume
  const volume = live.reduce((s, d) => s + (dealPrice(d) || 0), 0);
  const uc = live.filter((d) => ['contract', 'ctc'].includes(key(d)));
  const pendingVolume = uc.reduce((s, d) => s + (dealPrice(d) || 0), 0);
  // RW-3766: each deal's own projected GCI (dealProjectedGci), weighted by stage
  const gci = live.reduce((s, d) => s + (dealProjectedGci(d, { defaultRatePct }) || 0) * (weights[key(d)] ?? 0), 0);
  // RW-3413: the Board's number IS the length of Home's list (it drills there)
  const needs = computeNeedsYou(deals, { toCheck }).length;
  return { active: live.length, volume, ucCount: uc.length, pendingVolume, gci, needs };
}

/**
 * RW-3413: the ONE Needs-you list. Deals Home itemizes it and the Board's
 * pulse shows its length — the Board used to count deals by
 * dealNeedsAttention (overdue tasks included) while Home listed only held,
 * changes and review rows, so "Needs you → 9" opened a list of 3.
 * Kinds: held / changes / overdue carry their `deal`; check is ONE row with
 * a `count`. Presentation (titles, links) stays with the screen.
 *
 * RW-3624: `toCheck` — the agents' ticks waiting on this viewer to check them
 * (My Tasks' "To check"), the one thing on the page that waits on nobody else.
 * RW-3418: files in broker review are no longer here — nothing for the agent
 * to do while the broker reads; countInBrokerReview is the tracking number.
 */
export function computeNeedsYou(allDeals, { toCheck = 0 } = {}) {
  const deals = ownBook(allDeals);
  const items = [
    ...deals.filter((d) => d.payout_held === true).map((d) => ({ id: `held-${d.id}`, kind: 'held', deal: d })),
    ...deals.filter((d) => d.compliance === 'changes').map((d) => ({ id: `changes-${d.id}`, kind: 'changes', deal: d })),
    ...deals.filter((d) => (d.overdue_tasks || 0) > 0).map((d) => ({ id: `overdue-${d.id}`, kind: 'overdue', deal: d })),
  ];
  if (toCheck > 0) items.push({ id: 'check', kind: 'check', deal: null, count: toCheck });
  return items;
}

/** RW-3418: the viewer's files sitting in broker review — tracked, not a to-do. */
export function countInBrokerReview(allDeals) {
  return ownBook(allDeals).filter((d) => d.compliance === 'review').length;
}

/**
 * RW-3412: a deal's day ('YYYY-MM-DD') as "Oct 15", with the year when it is
 * not this year ("Oct 15, 2025") — a year-old closing read as five days ago.
 */
export function formatDealDay(ymd, now = new Date()) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(y !== now.getFullYear() ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
}

/**
 * RW-3419: the ONE "closed this year" rule — Closed YTD and Deals Home's
 * "Closed this year" row both read it. A closed deal counts in `year` (default
 * this year) when its closing_date falls in it. The row used to count every
 * closed deal the year's read returned, which also holds a deal closed with
 * no closing date and one whose outcome landed this week (the list's 7-day
 * bridge), so it read 1170 beside Closed YTD's 1169. (Leadership's SQL dates
 * a closing by closing_date too.)
 */
export function closedInYear(deal, year) {
  const y = String(year || new Date().getFullYear());
  return !!deal && deal.outcome === 'closed'
    && typeof deal.closing_date === 'string' && deal.closing_date.startsWith(y);
}

/**
 * RW-927 residue: the closed-YTD rollup. Deals with outcome 'closed' whose
 * closing_date (the field listDeals returns; outcome_at is not exposed) falls
 * in `year` — defaults to the current year. Volume via dealValue (sale_price
 * first). RW-3861: gci is OUR side's gross (dealProjectedGci — a closed
 * deal's deal_financials.our_side_gross), the figure Leadership and "Proj.
 * GCI" use; it summed agent_gross, the agent's take after the split, so the
 * tile read low against Leadership for the same closings. A scoped caller's
 * tile still counts only their own book (the list enrichment is staff-or-self).
 * RW-3419: which deals count is closedInYear. RW-3623: a caller slicing by
 * class filters first (dealClass dealInClass — fee rows are in All only).
 */
export function computeYtdClosed(deals, { year } = {}) {
  const closed = ownBook(deals).filter((d) => closedInYear(d, year));
  const volume = closed.reduce((s, d) => s + (dealValue(d) || 0), 0);
  const gci = closed.reduce((s, d) => s + (dealProjectedGci(d) || 0), 0);
  return { count: closed.length, volume, gci };
}

/** A deal that should interrupt someone's morning (Needs-you semantics). */
export function dealNeedsAttention(d) {
  return d.payout_held === true || d.compliance === 'changes'
    || (d.overdue_tasks || 0) > 0 || d.darwin === 'validation_failed';
}

// ── Darwin sync surface states ──────────────────────────────────────────
// RW-3684: agent words — "Synced" / "Not pushed" was system jargon.
export const DA_DARWIN = {
  synced:            { label: 'Sent to Darwin' },
  not_pushed:        { label: 'Not sent yet' },
  validation_failed: { label: 'Missing fields' },
};

// ── tag taxonomy (RW-1091 — 4 categories, tags DO things) ───────────────
export const DA_TAG_CATS = {
  source:    { label: 'Source & Referral', color: DA_COLORS.indigo },
  condition: { label: 'Deal Conditions',   color: DA_COLORS.grey },
  financial: { label: 'Financial',         color: DA_COLORS.moss },
  attention: { label: 'Attention',         color: DA_COLORS.coralInk },
};

export const DA_TAGS = {
  incoming_referral:   { label: 'Incoming Referral',   cat: 'source' },
  outgoing_referral:   { label: 'Outgoing Referral',   cat: 'source' },
  leadingre:           { label: 'LeadingRE',           cat: 'source' },
  relocation:          { label: 'Relocation',          cat: 'source' },
  past_client:         { label: 'Past Client',         cat: 'source' },
  open_house:          { label: 'Open House',          cat: 'source' },
  sphere:              { label: 'Sphere / SOI',        cat: 'source' },
  cash:                { label: 'Cash',                cat: 'condition' },
  financed:            { label: 'Financed',            cat: 'condition' },
  new_construction:    { label: 'New Construction',    cat: 'condition' },
  short_sale:          { label: 'Short Sale',          cat: 'condition' },
  reo:                 { label: 'REO',                 cat: 'condition' },
  exchange_1031:       { label: '1031 Exchange',       cat: 'condition' },
  contingent:          { label: 'Contingent',          cat: 'condition' },
  vacant_land:         { label: 'Vacant Land',         cat: 'condition' },
  referral_fee_due:    { label: 'Referral Fee Due',    cat: 'financial' },
  commission_received: { label: 'Commission Received', cat: 'financial' },
  awaiting_file:       { label: 'Awaiting File',       cat: 'financial' },
  vip:                 { label: 'VIP',                 cat: 'attention' },
  rush:                { label: 'Rush',                cat: 'attention' },
  at_risk:             { label: 'At Risk',             cat: 'attention' },
  needs_docs:          { label: 'Needs Docs',          cat: 'attention' },
};
